import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useAlert from "src/hooks/use-alert";
import { useInviteTalentMutation } from "src/lib/endpoints/hire/hire.api";
import DateTime from "src/lib/utils/date-time";
import ModalContainer from ".";
import { ScrollArea } from "../ui/scroll-area";
import { JobDetail } from "src/lib/endpoints/hire/hire.type";
import { useGetPaymentAccountInfo } from "src/lib/endpoints/wallet/wallet.api";
import { commaSeperatedNumberFormat } from "src/lib/utils/format-text";
import { Copy } from "lucide-react";
import { Project } from "src/lib/endpoints/projects/project.type";
import LoadingSpinner from "../Loader/LoadingSpinner";
import Button from "../Button";
import { assets } from "src/lib/utils/assets";
import { useTypedSelector } from "src/lib/store/store";
import { selectUserState } from "src/lib/features/auth/authSlice";

enum FieldsName {
  DATE = "date",
  TIME = "time",
  LINK = "link",
}

interface InputFields {
  date: string;
  time: string;
  link: string;
}

export type PaymentPlans = "basic" | "enterprise";

type Props = {
  open: boolean;
  type: string;
  // paymentPlan?: PaymentPlans;
  close?: () => void;
  job?: JobDetail | undefined;
  jobId?: string;
  userId?: string;
  info?: string;
  label?: string;
  project?: Project | undefined;
  callback?: () => void;
  confirmDeposit?: () => void;
  amount?: string | number;
  loading?: boolean;
};

const MakeDepositModal = ({
  open,
  close,
  userId,
  job,
  jobId,
  project,
  type,
  // paymentPlan = "basic",
  info,
  label,
  amount,
  loading,
  callback,
  confirmDeposit,
}: Props) => {
  const { state, open: openAlert, close: closeAlert, Alert } = useAlert();
  
  const generatedJobId = jobId ?? job?.id ?? (job as any)?.job_detail_id ?? "";
  
  const automaticWorkType = (
    job?.workType ||
    job?.jobDetailProjDetail?.work_type ||
    "fulltime"
  ).toLowerCase().trim();

  const { paymentAccountInfo } = useGetPaymentAccountInfo(
    generatedJobId,
    type,
    // paymentPlan,
    "basic",
    amount?.toString(),
    // project?.id ?? '1'
    project?.id ?? undefined,
    automaticWorkType
  );


     const { user } = useTypedSelector(selectUserState);

  useEffect(() => {
    if (
      paymentAccountInfo &&
      +(paymentAccountInfo?.extra_info?.amount?.value || 0) < 1
    ) {
      callback && callback();
    }
  }, [paymentAccountInfo]);

  useEffect(() => {
    if (!close) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);

    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [close]);

  // console.log('Account info:', paymentAccountInfo)

  return (
    <div className="w-full">
      <ModalContainer open={open} close={close}>
        <ScrollArea className="p-3 md:p-5 w-[90vw] md:w-[55vw] h-[80vh] flex justify-center items-center">
          <div className="">
            {!paymentAccountInfo ? (
              <div className="flex flex-col space-y-3 min-h-[70vh] w-full items-center justify-center">
                <LoadingSpinner />
                <p>Fetching payment information</p>
              </div>
            ) : (
              <div className="flex flex-col space-y-5 justify-center place-self-center items-center h-full">
                <div className="w-16">
                  <img
                    src={assets.Logo}
                    alt="droomwork brand logo"
                    className="w-full h-full"
                  />
                </div>

                <p className="text-lg text-wrap md:text-2xl font-bold">
                  {label}
                </p>

                {info && (
                  <p className="text-center text-wrap w-[80%] text-sm md:text-base">
                    {info}
                  </p>
                )}

                <div className="grid grid-cols-2 text-lg font-medium text-muted-foreground space-y-2 items-center justify-center ">
                  <p className="col-span-1">Account Name:</p>
                  <div className="col-span-1 flex items-center space-x-3">
                    <p className="capitalize font-semibold text-muted-foreground">
                      {paymentAccountInfo?.account?.account_name}
                    </p>
                    <span
                      className="hover:cursor-pointer"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          paymentAccountInfo?.account?.account_name ?? ""
                        );
                        openAlert({
                          ...state,
                          title: "Copied",
                          variant: "success",
                          active: true,
                        });
                      }}
                    >
                      <Copy color="gray" size={20} />
                    </span>
                  </div>

                  <p className="col-span-1">Account Number:</p>
                  <div className="col-span-1 flex items-center space-x-3">
                    <p className="capitalize font-semibold text-muted-foreground">
                      {paymentAccountInfo?.account?.account_number}
                    </p>
                    <span
                      className="hover:cursor-pointer"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          paymentAccountInfo?.account?.account_number ?? ""
                        );
                        openAlert({
                          ...state,
                          title: "Copied",
                          variant: "success",
                          active: true,
                        });
                      }}
                    >
                      <Copy color="gray" size={20} />
                    </span>
                  </div>

                  <p className="col-span-1">Bank Name:</p>
                  <div className="col-span-1 flex items-center space-x-3">
                    <p className="capitalize font-semibold text-muted-foreground">
                      {paymentAccountInfo?.account?.bank_name}
                    </p>
                    <span
                      className="hover:cursor-pointer"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          paymentAccountInfo?.account?.bank_name ?? ""
                        );
                        openAlert({
                          ...state,
                          title: "Copied",
                          variant: "success",
                          active: true,
                        });
                      }}
                    >
                      <Copy color="gray" size={20} />
                    </span>
                  </div>

                    {user?.userCompany?.currency_symbol === "$" ? (
                      
                      <>
                        <p className="col-span-1">Routing Number:</p>
                        <div className="col-span-1 flex items-center space-x-3">
                          <p className="capitalize font-semibold text-muted-foreground">
                            {paymentAccountInfo?.account?.routing_number}
                          </p>
                          <span
                            className="hover:cursor-pointer"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                paymentAccountInfo?.account?.routing_number ?? ""
                              );
                              openAlert({
                                ...state,
                                title: "Copied",
                                variant: "success",
                                active: true,
                              });
                            }}
                          >
                            <Copy color="gray" size={20} />
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="col-span-1">Payment Code:</p>
                        <div className="col-span-1 flex items-center space-x-3">
                          <p className="capitalize font-semibold text-muted-foreground">
                            {paymentAccountInfo?.extra_info?.payment_code}
                          </p>
              
                          <span
                            className="hover:cursor-pointer"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                paymentAccountInfo?.extra_info?.payment_code ?? ""
                              );
                              openAlert({
                                ...state,
                                title: "Copied",
                                variant: "success",
                                active: true,
                              });
                            }}
                          >
                            <Copy color="gray" size={20} />
                          </span>
                        </div>
                      </>
                    )}
                  

                  <p className="col-span-1">Amount:</p>
                  <div className="col-span-1 flex items-center space-x-3">
                    <p className="capitalize font-semibold text-muted-foreground">
               { user?.userCompany?.currency_symbol  ?? '₦' }                                                                                                                                                                            
                      {commaSeperatedNumberFormat(
                        paymentAccountInfo?.extra_info?.amount?.value || 0
                      )}
                      
                    </p>
                    <span                                                                                                                                     
                      className="hover:cursor-pointer"
                      onClick={() => {

                        const value = paymentAccountInfo?.extra_info?.amount?.value;
                        if (!value) return;
                        navigator.clipboard.writeText(
                          "" +
                            paymentAccountInfo?.extra_info?.equivalent_amount
                              .value
                        );
                        openAlert({
                          ...state,
                          title: "Copied",
                          variant: "success",
                          active: true,
                        });
                      }}
                    >
                      <Copy color="gray" size={20} />
                    </span>
                  </div>
                </div>

                <p className="w-[90%] text-wrap text-sm italic text-muted-foreground">
                  <span className="font-medium text-black">Important:</span>{" "}
                  Please use the payment code as the narration when making your
                  payment. This ensures we can verify your transaction quickly
                  and accurately. Once the payment is completed, your request
                  will be processed automatically within a few minutes.
                </p>

                <Button
                  className="w-[90%] md:w-[70%]"
                  label={`I have sent 

                               ${ user?.userCompany?.currency_symbol }${commaSeperatedNumberFormat(
                    paymentAccountInfo?.extra_info?.amount?.value || 0
                  )}`}
                  disabled={true}
                />
              </div>
            )}
          </div>
        </ScrollArea>
      </ModalContainer>

      <span className="fixed right-3 top-3 z-30">
        <Alert />
      </span>
    </div>
  );
};

export default MakeDepositModal;
